import config from '../config/environment';
import type { Address, PhoneNumber, Student } from '../types';
import { handleServiceError } from '../utils/errorHandler';
import { sanitizePublicLinkError } from '../utils/publicLinkErrors';

export interface PublicStudentFormsStatus {
  has_anamnesis: boolean;
  has_parq: boolean;
  can_submit_anamnesis: boolean;
  can_submit_parq: boolean;
  last_complete_anamnesis_at: string | null;
  last_complete_parq_at: string | null;
}

export interface PublicStudentContext {
  student: Student;
  phone: PhoneNumber | null;
  address: Address | null;
  formsStatus: PublicStudentFormsStatus;
  photoCycleStartedOn: string | null;
}

interface ResolvePublicStudentContextResponse {
  success?: boolean;
  error?: string;
  code?: string;
  data?: {
    student: Student;
    phone?: PhoneNumber | null;
    address?: Address | null;
    formsStatus?: Partial<PublicStudentFormsStatus>;
    photoCycleStartedOn?: string | null;
  };
}

export class PublicStudentTokenAlreadyUsedError extends Error {
  readonly code = 'TOKEN_ALREADY_USED';

  constructor(message = 'Já recebemos suas fotos. Obrigada!') {
    super(message);
    this.name = 'PublicStudentTokenAlreadyUsedError';
  }
}

class PublicStudentContextServiceClass {
  async resolve(token: string): Promise<PublicStudentContext> {
    if (!config.supabaseFunctionsUrl) {
      handleServiceError(
        new Error('URL das Edge Functions não configurada.'),
        'carregar dados da aluna',
      );
    }

    const response = await fetch(`${config.supabaseFunctionsUrl}/resolve-public-student-context`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({ token }),
    });

    const body = await response.json().catch(() => ({})) as ResolvePublicStudentContextResponse;

    if (
      response.status === 409
      || body.code === 'TOKEN_ALREADY_USED'
      || /já recebemos suas fotos/i.test(body.error || '')
    ) {
      throw new PublicStudentTokenAlreadyUsedError(body.error);
    }

    if (!response.ok || body.success === false || !body.data?.student) {
      throw new Error(sanitizePublicLinkError(body.error || 'Não foi possível carregar os dados da aluna.'));
    }

    return {
      student: body.data.student,
      phone: body.data.phone ?? null,
      address: body.data.address ?? null,
      formsStatus: (() => {
        const raw = body.data.formsStatus;
        const hasAnamnesis = raw?.has_anamnesis === true;
        const hasParq = raw?.has_parq === true;

        return {
          has_anamnesis: hasAnamnesis,
          has_parq: hasParq,
          can_submit_anamnesis: typeof raw?.can_submit_anamnesis === 'boolean'
            ? raw.can_submit_anamnesis
            : !hasAnamnesis,
          can_submit_parq: typeof raw?.can_submit_parq === 'boolean'
            ? raw.can_submit_parq
            : !hasParq,
          last_complete_anamnesis_at: typeof raw?.last_complete_anamnesis_at === 'string'
            ? raw.last_complete_anamnesis_at
            : null,
          last_complete_parq_at: typeof raw?.last_complete_parq_at === 'string'
            ? raw.last_complete_parq_at
            : null,
        };
      })(),
      photoCycleStartedOn: typeof body.data.photoCycleStartedOn === 'string'
        && /^\d{4}-\d{2}-\d{2}$/.test(body.data.photoCycleStartedOn)
        ? body.data.photoCycleStartedOn
        : null,
    };
  }
}

export const PublicStudentContextService = new PublicStudentContextServiceClass();
